import React, { useState, useEffect } from 'react';
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup, sendEmailVerification, signOut, deleteUser } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc } from 'firebase/firestore';
import { app } from './firebaseConfig'; // Import your Firebase app instance

const auth = getAuth(app);
const db = getFirestore(app);
const googleProvider = new GoogleAuthProvider();

const AuthPage = ({ onAuthSuccess }) => {
  const [isSignUp, setIsSignUp] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [salespersonFirstName, setSalespersonFirstName] = useState('');
  const [salespersonLastName, setSalespersonLastName] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setError('');
    setMessage('');
  }, [isSignUp]); // Clear messages when switching between sign-up/login

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      if (isSignUp) {
        if (password !== confirmPassword) {
          setError("Passwords do not match.");
          setLoading(false);
          return;
        }
        if (!salespersonFirstName || !salespersonLastName) {
          setError("Salesperson's first name and last name are required.");
          setLoading(false);
          return;
        }

        console.log("AuthPage.js: Attempting to sign up with email:", email);
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        console.log("AuthPage.js: Sign up successful! User UID:", user.uid);

        // Send email verification
        await sendEmailVerification(user);
        setMessage('Account created! Please verify your email. A verification link has been sent.');
        console.log("AuthPage.js: Verification email sent.");

        // Create user profile in Firestore
        await setDoc(doc(db, 'users', user.uid), {
          email: user.email,
          createdAt: new Date(),
          salesperson: {
            firstName: salespersonFirstName,
            lastName: salespersonLastName,
            fullName: `${salespersonFirstName} ${salespersonLastName}` // Store full name for easy lookup
          },
          paymentStatus: 'pending', // Default status
          membershipExpiry: null // No expiry initially
        });
        console.log("AuthPage.js: User profile created in Firestore.");


        // Create or update salesperson document in Firestore
        const salespersonRef = doc(db, 'salespersons', `${salespersonFirstName} ${salespersonLastName}`);
        const salespersonDoc = await getDoc(salespersonRef);

        if (!salespersonDoc.exists()) {
          await setDoc(salespersonRef, {
            firstName: salespersonFirstName,
            lastName: salespersonLastName,
            fullName: `${salespersonFirstName} ${salespersonLastName}`,
            currentMonthEarnings: 0,
            totalSales: 0,
            lastUpdated: new Date()
          });
          console.log("AuthPage.js: Salesperson document created in Firestore.");
        } else {
          console.log("AuthPage.js: Salesperson document already exists. No new creation.");
        }

        onAuthSuccess(user); // Notify App.js of successful authentication
        console.log("AuthPage.js: onAuthSuccess called for signup.");

      } else { // Login Flow
        console.log("AuthPage.js: Attempting to log in with email:", email);
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        console.log("AuthPage.js: Login successful! User UID:", user.uid, "Email Verified:", user.emailVerified);

        if (!user.emailVerified) {
          setError('Please verify your email before logging in. Check your inbox for a verification link.');
          // await signOut(auth); // TEMPORARILY COMMENTED OUT FOR LOCAL TESTING
          console.log("AuthPage.js: Email not verified. Sign out prevented for local testing.");
          setLoading(false);
          return;
        }
        console.log("AuthPage.js: Email verified. Calling onAuthSuccess for login.");
        onAuthSuccess(user); // Notify App.js of successful authentication
      }
    } catch (err) {
      console.error("AuthPage.js: Auth error:", err.code, err.message);
      if (err.code === 'auth/email-already-in-use') {
        setError('This email is already in use. Please log in or use a different email.');
      } else if (err.code === 'auth/invalid-email' || err.code === 'auth/weak-password' || err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Invalid email or password.');
      } else {
        setError(`Authentication failed: ${err.message}`);
      }
    } finally {
      setLoading(false);
      console.log("AuthPage.js: handleAuth finished. Loading state set to false.");
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      console.log("AuthPage.js: Attempting Google Sign-in.");
      const userCredential = await signInWithPopup(auth, googleProvider);
      const user = userCredential.user;
      console.log("AuthPage.js: Google Sign-in successful! User UID:", user.uid);

      // Check if user exists in Firestore, if not, create their profile
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        await setDoc(userRef, {
          email: user.email,
          createdAt: new Date(),
          // For Google Sign-in, we don't have salesperson fields directly.
          // You might prompt them for this info on first login or set a default.
          salesperson: {
            firstName: 'N/A',
            lastName: 'N/A',
            fullName: 'Google User'
          },
          paymentStatus: 'pending',
          membershipExpiry: null
        });
        console.log("AuthPage.js: Firestore profile created for Google user.");
      } else {
        console.log("AuthPage.js: Firestore profile already exists for Google user.");
      }
      onAuthSuccess(user); // Notify App.js of successful authentication
      console.log("AuthPage.js: onAuthSuccess called for Google Sign-in.");

    } catch (err) {
      console.error("AuthPage.js: Google Sign-in error:", err.code, err.message);
      setError(`Google Sign-in failed: ${err.message}`);
    } finally {
      setLoading(false);
      console.log("AuthPage.js: handleGoogleSignIn finished. Loading state set to false.");
    }
  };

  const handleDeleteUser = async () => {
    // IMPORTANT: In a real app, use a custom modal instead of window.confirm
    if (window.confirm('Are you sure you want to delete your account? This action is irreversible.')) {
      setLoading(true);
      try {
        console.log("AuthPage.js: Attempting to delete current user.");
        await deleteUser(auth.currentUser);
        setMessage('Account deleted successfully.');
        console.log("AuthPage.js: User account deleted.");
        // onAuthStateChanged in App.js will handle setting user to null
      } catch (err) {
        console.error("AuthPage.js: Error deleting user:", err.code, err.message);
        setError(`Failed to delete account: ${err.message}. Please log out and log in again, then try to delete.`);
      } finally {
        setLoading(false);
        console.log("AuthPage.js: handleDeleteUser finished. Loading state set to false.");
      }
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 font-inter">
      <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md rounded-lg">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">
          {isSignUp ? 'Create Your Account' : 'Login to Your Account'}
        </h2>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        {message && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md relative mb-4" role="alert">
            <span className="block sm:inline">{message}</span>
          </div>
        )}

        <form onSubmit={handleAuth}>
          <div className="mb-4">
            <label htmlFor="email" className="block text-gray-700 text-sm font-bold mb-2">Email</label>
            <input
              type="email"
              id="email"
              className="shadow appearance-none border rounded-md w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent rounded-md"
              placeholder="email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="password" className="block text-gray-700 text-sm font-bold mb-2">Password (min 6 characters)</label>
            <input
              type="password"
              id="password"
              className="shadow appearance-none border rounded-md w-full py-3 px-4 text-gray-700 mb-3 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent rounded-md"
              placeholder="********"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          {isSignUp && (
            <>
              <div className="mb-4">
                <label htmlFor="confirmPassword" className="block text-gray-700 text-sm font-bold mb-2">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  className="shadow appearance-none border rounded-md w-full py-3 px-4 text-gray-700 mb-3 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent rounded-md"
                  placeholder="********"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <div className="mb-4">
                <label htmlFor="salespersonFirstName" className="block text-gray-700 text-sm font-bold mb-2">Salesperson First Name</label>
                <input
                  type="text"
                  id="salespersonFirstName"
                  className="shadow appearance-none border rounded-md w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent rounded-md"
                  placeholder="John"
                  value={salespersonFirstName}
                  onChange={(e) => setSalespersonFirstName(e.target.value)}
                  required
                />
              </div>
              <div className="mb-6">
                <label htmlFor="salespersonLastName" className="block text-gray-700 text-sm font-bold mb-2">Salesperson Last Name</label>
                <input
                  type="text"
                  id="salespersonLastName"
                  className="shadow appearance-none border rounded-md w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent rounded-md"
                  placeholder="Doe"
                  value={salespersonLastName}
                  onChange={(e) => setSalespersonLastName(e.target.value)}
                  required
                />
              </div>
            </>
          )}
          <div className="flex items-center justify-between">
            <button
              type="submit"
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-75 transition duration-200 ease-in-out rounded-md"
              disabled={loading}
            >
              {loading ? (isSignUp ? 'Signing Up...' : 'Logging In...') : (isSignUp ? 'Sign Up' : 'Login')}
            </button>
          </div>
        </form>

        <div className="text-center mt-4">
          <span className="text-gray-600">
            {isSignUp ? 'Already have an account?' : 'Don\'t have an account?'}
          </span>{' '}
          <button
            onClick={() => setIsSignUp(!isSignUp)}
            className="text-blue-500 hover:text-blue-700 font-semibold focus:outline-none"
          >
            {isSignUp ? 'Login' : 'Sign Up'}
          </button>
        </div>

        <div className="my-6 border-b border-gray-200"></div>

        <button
          onClick={handleGoogleSignIn}
          className="w-full flex items-center justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75 transition duration-200 ease-in-out rounded-md mb-3"
          disabled={loading}
        >
          <svg className="w-5 h-5 mr-2" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
            <path d="M19.20 10.27c0-.62-.05-1.22-.16-1.81H10v3.45h5.19c-.23 1.15-.9 2.13-1.9 2.78v2.24h2.88a9.927 9.927 0 002.63-7.55z" fill="#4285F4"></path>
            <path d="M10 20c2.72 0 5-1 6.67-2.73l-2.88-2.24c-.75.5-1.7.8-2.67.8-2.06 0-3.8-1.39-4.42-3.26H2.63v2.3C4.24 18.26 6.91 20 10 20z" fill="#34A853"></path>
            <path d="M5.58 11.96c-.19-.5-.28-1.03-.28-1.58s.09-1.08.28-1.58V6.44H2.63A9.927 9.927 0 000 10c0 1.5.36 2.92.99 4.14l2.63-2.07c-.4-.73-.62-1.58-.62-2.45z" fill="#FBBC04"></path>
            <path d="M10 4.14c1.47 0 2.76.49 3.78 1.41l2.42-2.42C15 1.54 12.72 0 10 0 6.91 0 4.24 1.74 2.63 4.7l2.95 2.3C6.2 5.53 7.94 4.14 10 4.14z" fill="#EA4335"></path>
          </svg>
          Sign in with Google
        </button>
        <button
          onClick={() => setError('Facebook Sign-in not implemented yet.')}
          className="w-full flex items-center justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75 transition duration-200 ease-in-out rounded-md"
          disabled={loading}
        >
          <svg className="w-5 h-5 mr-2" aria-hidden="true" fill="currentColor" viewBox="0 0 24 24">
            <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.262c-1.225 0-1.628.75-1.628 1.549V12h2.773l-.443 2.891h-2.33v6.988C18.343 21.128 22 16.991 22 12z"></path>
          </svg>
          Sign in with Facebook
        </button>

        {auth.currentUser && (
          <div className="mt-6 text-center">
            <button
              onClick={handleDeleteUser}
              className="text-red-500 hover:text-red-700 text-sm font-semibold focus:outline-none"
              disabled={loading}
            >
              Delete My Account
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthPage;
